import { store } from '../Components/Store/store';
import { setFile } from '../Components/Store/LoadReducer';

export class InMemoryStorage {
    constructor() {
      this.files = new Map();
      this.isInit = false;
    }
  
    async init() {
      this.isInit = true;
    }
  
    async saveFile(fileName, data) {
      const fileData = data instanceof File ? data : new File([data], fileName);
      const link = URL.createObjectURL(fileData);
      this.files.set(fileName, fileData);
      store.dispatch(setFile({ name: fileName, link: link }));
      return { name: fileName, link };
    }
  
    async loadFile(fileName) {
      const data = this.files.get(fileName);
      return data ? { name: fileName, data } : undefined;
    }
  
    async removeFileByName(fileName) {
      this.files.delete(fileName);
    }
  
    async removeAllFiles() {
      this.files.clear();
    }
  
    async listFiles() {
      return Array.from(this.files.keys());
    }
  
    async getFileUrl(fileName) {
      const file = await this.loadFile(fileName);
      if (!file) return "";
      return URL.createObjectURL(file.data);
    }
}

/**
 * Wrap an IndexedDB request in a Promise for easy async/await usage.
 */
function promisifyRequest(request) {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
}
  
/**
 * Wait until a transaction completes (or fails).
 */
function promisifyTransaction(transaction) {
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
}

export class IndexedDBStorage {
    constructor() {
      this.dbName = 'file_storage';
      this.storageName = 'files';
      this.db = null;
      this.isInit = false;
    }
  
    /**
     * Initialize the DB and create the object store if needed.
     */
    async init() {
      this.db = await this._openDB(this.dbName, 1);
      this.isInit = true;
      // Once opened, if `onupgradeneeded` ran, we have our store created.
      // Otherwise, it existed already.
    }
  
    /**
     * Open an IndexedDB database (version = 1).
     * Creates `files` object store if it doesn't exist, via onupgradeneeded.
     */
    _openDB(name, version) {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(name, version);
        
        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains(this.storageName)) {
            db.createObjectStore(this.storageName, { keyPath: 'name' });
          }
        };
  
        request.onsuccess = (event) => {
          resolve(event.target.result);
        };
  
        request.onerror = () => {
          reject(request.error);
        };
      });
    }
  
    async saveFile(fileName, data) {
      const fileData = data instanceof File ? data : new File([data], fileName);
      const tx = this.db.transaction([this.storageName], 'readwrite');
      const storage = tx.objectStore(this.storageName);
      const link = URL.createObjectURL(fileData);
      store.dispatch(setFile({ name: fileName, link: link }));
      storage.put({ name: fileName, data: fileData });
      await promisifyTransaction(tx);
    }
  
    async loadFile(fileName) {
      const tx = this.db.transaction([this.storageName], 'readonly');
      const storage = tx.objectStore(this.storageName);
      const request = storage.get(fileName);
      const result = await promisifyRequest(request);
      return result;
    }
  
    async removeFileByName(fileName) {
      const tx = this.db.transaction([this.storageName], 'readwrite');
      const storage = tx.objectStore(this.storageName);
      storage.delete(fileName);
      await promisifyTransaction(tx);
    }
  
    async removeAllFiles() {
      const tx = this.db.transaction([this.storageName], 'readwrite');
      const storage = tx.objectStore(this.storageName);
      storage.clear();
      await promisifyTransaction(tx);
    }
  
    async listFiles() {
      // We'll use getAll(), supported in most modern browsers
      const tx = this.db.transaction([this.storageName], 'readonly');
      const storage = tx.objectStore(this.storageName);
      const request = storage.getAll();
      const allEntries = await promisifyRequest(request); 
      // allEntries is an array of {name, data}
      return allEntries.map(entry => entry.name);
    }
  
    async getFileUrl(fileName) {
      const file = await this.loadFile(fileName);
      if (!file) return "";
      return URL.createObjectURL(file.data);
    }
}

